import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Brain, Palette, Code, Smartphone, Rocket, CheckCircle2, Loader2, ExternalLink } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sidebar } from '../components/Sidebar';
import { TopBar } from '../components/TopBar';
import { Progress } from '../components/ui/progress';
import { buildSteps } from '../data/mockData';
import API from '../lib/api';

export default function BuildStatus() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [build, setBuild] = useState(null);
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);

  const prompt = build?.prompt || "Building your app...";

  const iconMap = {
    Brain,
    Palette,
    Code,
    Smartphone,
    Rocket
  };

  // ✅ SINGLE CLEAN API POLLING
  useEffect(() => {
    const fetchBuild = async () => {
      try {
        const token = localStorage.getItem("token");

        const res = await API.get(`/builds/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const buildData = res.data.build;

        setBuild(buildData);

        // ✅ progress from backend
        const prog = buildData.progress || 0;
        setProgress(prog);

        // ✅ step mapping
        const step = Math.floor((prog / 100) * buildSteps.length);
        setCurrentStep(step);

        // ✅ redirect when done
        if (buildData.status === "completed") {
          setTimeout(() => {
            navigate(`/builds/${buildData.id}`);
          }, 1500);
        }

      } catch (err) {
        console.error("Fetch build error:", err);
      }
    };

    fetchBuild(); // first call

    const interval = setInterval(fetchBuild, 2000); // polling

    return () => clearInterval(interval);
  }, [id, navigate]);

  // ✅ LOADING STATE
  if (!build) {
    return (
      <div className="text-white p-10 text-center">
        <Loader2 className="animate-spin mx-auto mb-4" />
        Loading build...
      </div>
    );
  }
  return (
    <div className="dark min-h-screen bg-background text-foreground">
      <Sidebar />
      
      <div className="md:ml-64">
        <TopBar />
        
        <main className="p-6 md:p-12 max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-12"
          >
            <div className="text-center space-y-4">
              <h1
                className="text-3xl md:text-5xl font-semibold tracking-tight text-white"
                style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                data-testid="build-status-title"
              >
                Building Your App
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto" data-testid="build-prompt">
                {prompt}
              </p>
            </div>

            <div className="space-y-8">
              <div className="space-y-3" data-testid="progress-bar-container">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Overall Progress</span>
                  <span className="text-white font-medium">{Math.round(progress)}%</span>
                </div>
                <Progress value={progress} className="h-2" data-testid="progress-bar" />
              </div>

              <div className="relative" data-testid="build-steps">
                <div className="absolute top-12 left-0 w-full h-0.5 bg-white/10 -z-10"></div>
                
                <div className="flex items-start justify-between w-full">
                  {buildSteps.map((step, index) => {
                    const Icon = iconMap[step.icon];
                    const isCompleted = index < currentStep;
                    const isCurrent = index === currentStep;
                    const isPending = index > currentStep;

                    return (
                      <motion.div
                        key={step.id}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.1 }}
                        className="flex flex-col items-center gap-3 flex-1"
                        data-testid={`build-step-${index}`}
                      >
                        <div
                          className={`relative w-20 h-20 rounded-2xl flex items-center justify-center transition-all duration-500 ${
                            isCompleted
                              ? 'bg-primary/20 border-2 border-primary shadow-lg shadow-primary/20'
                              : isCurrent
                              ? 'bg-accent/20 border-2 border-accent shadow-lg shadow-accent/20'
                              : 'bg-white/5 border-2 border-white/10'
                          }`}
                        >
                          <AnimatePresence mode="wait">
                            {isCompleted ? (
                              <motion.div
                                key="completed"
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                exit={{ scale: 0 }}
                              >
                                <CheckCircle2 className="w-8 h-8 text-primary" />
                              </motion.div>
                            ) : isCurrent ? (
                              <motion.div
                                key="current"
                                initial={{ scale: 0 }}
                                animate={{ scale: 1, rotate: 360 }}
                                transition={{ rotate: { duration: 2, repeat: Infinity, ease: 'linear' } }}
                              >
                                <Loader2 className="w-8 h-8 text-accent" />
                              </motion.div>
                            ) : (
                              <Icon className="w-8 h-8 text-muted-foreground" />
                            )}
                          </AnimatePresence>
                        </div>

                        <div className="text-center">
                          <p
                            className={`text-sm font-medium transition-colors ${
                              isCompleted || isCurrent ? 'text-white' : 'text-muted-foreground'
                            }`}
                          >
                            {step.label}
                          </p>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>

              {currentStep < buildSteps.length && (
                <motion.div
                  key={currentStep}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.4 }}
                  className="mt-12"
                  data-testid="step-preview-panel"
                >
                  {currentStep === 0 && <AnalyzingPromptPreview prompt={prompt} />}
                  {currentStep === 1 && <DesigningUIPreview />}
                  {currentStep === 2 && <GeneratingCodePreview />}
                  {currentStep === 3 && <BuildingAppsPreview />}
                  {currentStep === 4 && <DeployingPreviewUI />}
                </motion.div>
              )}

              {currentStep >= buildSteps.length && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center space-y-4 py-8"
                  data-testid="build-complete"
                >
                  <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                    <CheckCircle2 className="w-10 h-10 text-white" />
                  </div>
                  <h2 className="text-2xl font-semibold text-white" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                    Build Complete!
                  </h2>
                  <p className="text-muted-foreground">Redirecting to build details...</p>
                </motion.div>
              )}
            </div>
          </motion.div>
        </main>
      </div>
    </div>
  );
}

// Step 1: Analyzing Prompt Preview
const AnalyzingPromptPreview = ({ prompt }) => {
  const [dots, setDots] = React.useState('');

  React.useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => (prev.length >= 3 ? '' : prev + '.'));
    }, 500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="p-8 rounded-2xl bg-black/40 backdrop-blur-xl border border-white/10 shadow-2xl" data-testid="analyzing-preview">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
          <Brain className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-white" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            AI Analysis{dots}
          </h3>
          <p className="text-sm text-muted-foreground">Understanding your requirements</p>
        </div>
      </div>

      <div className="space-y-3 font-mono text-sm">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="flex items-start gap-3 text-green-400"
        >
          <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" />
          <div>
            <span className="text-muted-foreground">App Type:</span> <span className="text-white">Full-Stack Application</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
          className="flex items-start gap-3 text-green-400"
        >
          <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" />
          <div>
            <span className="text-muted-foreground">Platforms:</span> <span className="text-white">Web, Android, iOS</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.6 }}
          className="flex items-start gap-3 text-green-400"
        >
          <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" />
          <div>
            <div className="text-muted-foreground mb-2">Features Detected:</div>
            <div className="pl-4 space-y-1 text-white">
              <div>• User Authentication</div>
              <div>• Database Integration</div>
              <div>• API Services</div>
              <div>• Responsive UI</div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 1, repeat: Infinity }}
          className="flex items-center gap-2 pt-4 border-t border-white/5 text-accent"
        >
          <span className="inline-block w-2 h-2 rounded-full bg-accent animate-pulse"></span>
          <span>Analyzing architecture patterns...</span>
        </motion.div>
      </div>
    </div>
  );
};

// Step 2: Designing UI Preview
const DesigningUIPreview = () => {
  const screens = [
    { name: 'Home Screen', color: 'from-purple-500/20 to-blue-500/20' },
    { name: 'Login Screen', color: 'from-blue-500/20 to-cyan-500/20' },
    { name: 'Dashboard', color: 'from-cyan-500/20 to-teal-500/20' },
    { name: 'Settings', color: 'from-teal-500/20 to-green-500/20' }
  ];

  return (
    <div className="p-8 rounded-2xl bg-black/40 backdrop-blur-xl border border-white/10 shadow-2xl" data-testid="designing-preview">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center">
          <Palette className="w-5 h-5 text-accent" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-white" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Auto-Generated UI
          </h3>
          <p className="text-sm text-muted-foreground">Creating responsive layouts</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {screens.map((screen, index) => (
          <motion.div
            key={screen.name}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.2 }}
            className="group"
          >
            <div className={`aspect-[9/16] rounded-xl bg-gradient-to-br ${screen.color} border border-white/10 p-3 relative overflow-hidden`}>
              <div className="absolute inset-0 bg-white/5 backdrop-blur-sm"></div>
              
              <div className="relative z-10 space-y-2">
                <div className="h-2 w-3/4 bg-white/20 rounded"></div>
                <div className="h-2 w-1/2 bg-white/20 rounded"></div>
                <div className="mt-4 space-y-1">
                  <div className="h-12 bg-white/10 rounded"></div>
                  <div className="h-12 bg-white/10 rounded"></div>
                </div>
              </div>

              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
            </div>
            <p className="text-xs text-center mt-2 text-muted-foreground">{screen.name}</p>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

// Step 3: Generating Code Preview
const GeneratingCodePreview = () => {
  const codeLines = [
    'import React from "react";',
    'import { BrowserRouter, Routes } from "react-router-dom";',
    '',
    'export default function App() {',
    '  return (',
    '    <BrowserRouter>',
    '      <Routes>',
    '        <Route path="/" element={<Dashboard />} />',
    '        <Route path="/login" element={<Login />} />',
    '      </Routes>',
    '    </BrowserRouter>',
    '  );',
    '}'
  ];

  return (
    <div className="p-8 rounded-2xl bg-black/40 backdrop-blur-xl border border-white/10 shadow-2xl" data-testid="code-preview">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
            <Code className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Code Generation
            </h3>
            <p className="text-sm text-muted-foreground">Writing production-ready code</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="px-3 py-1 rounded-full bg-blue-500/10 text-blue-400 text-xs font-medium border border-blue-500/20">React</span>
          <span className="px-3 py-1 rounded-full bg-green-500/10 text-green-400 text-xs font-medium border border-green-500/20">Node</span>
          <span className="px-3 py-1 rounded-full bg-purple-500/10 text-purple-400 text-xs font-medium border border-purple-500/20">MongoDB</span>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-3 space-y-1">
          <div className="text-xs font-semibold text-muted-foreground mb-2">FILE TREE</div>
          <div className="space-y-1 text-xs font-mono">
            <div className="text-primary">📁 src/</div>
            <div className="pl-3 text-muted-foreground">📄 App.js</div>
            <div className="pl-3 text-muted-foreground">📄 index.js</div>
            <div className="pl-3 text-primary">📁 components/</div>
            <div className="pl-6 text-muted-foreground">📄 Dashboard.jsx</div>
            <div className="pl-6 text-muted-foreground">📄 Login.jsx</div>
          </div>
        </div>

        <div className="col-span-9">
          <div className="bg-black/60 rounded-lg p-4 border border-white/5 font-mono text-sm overflow-hidden">
            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-white/5">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-500/50"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500/50"></div>
                <div className="w-3 h-3 rounded-full bg-green-500/50"></div>
              </div>
              <span className="text-xs text-muted-foreground">App.js</span>
            </div>

            <div className="space-y-1">
              {codeLines.map((line, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="text-white/90"
                >
                  {line.includes('import') && (
                    <span>
                      <span className="text-purple-400">import</span> {line.substring(7)}
                    </span>
                  )}
                  {line.includes('export') && (
                    <span>
                      <span className="text-purple-400">export</span> <span className="text-purple-400">default</span> {line.substring(15)}
                    </span>
                  )}
                  {line.includes('return') && (
                    <span>  <span className="text-purple-400">return</span> {line.substring(9)}</span>
                  )}
                  {!line.includes('import') && !line.includes('export') && !line.includes('return') && line}
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.5, duration: 0.5, repeat: Infinity }}
                className="inline-block w-2 h-4 bg-primary ml-1"
              ></motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Step 4: Building Apps Preview
const BuildingAppsPreview = () => {
  const [statuses, setStatuses] = React.useState({
    apk: 'compiling',
    aab: 'pending',
    ipa: 'pending',
    web: 'pending'
  });

  React.useEffect(() => {
    const timers = [
      setTimeout(() => setStatuses(prev => ({ ...prev, apk: 'signing', aab: 'compiling' })), 800),
      setTimeout(() => setStatuses(prev => ({ ...prev, apk: 'optimizing', aab: 'signing', ipa: 'compiling' })), 1600),
      setTimeout(() => setStatuses(prev => ({ ...prev, apk: 'complete', aab: 'optimizing', ipa: 'signing', web: 'compiling' })), 2400)
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  const artifacts = [
    { id: 'apk', name: 'app-release.apk', icon: '📱', size: '45.2 MB' },
    { id: 'aab', name: 'app-release.aab', icon: '📱', size: '38.7 MB' },
    { id: 'ipa', name: 'app.ipa', icon: '🍎', size: '52.1 MB' },
    { id: 'web', name: 'web-build.zip', icon: '🌐', size: '12.5 MB' }
  ];

  const getStatusColor = (status) => {
    if (status === 'complete') return 'text-green-400 border-green-500/20 bg-green-500/10';
    if (status === 'pending') return 'text-muted-foreground border-white/5 bg-white/5';
    return 'text-accent border-accent/20 bg-accent/10';
  };

  const getStatusText = (status) => {
    if (status === 'complete') return 'Complete';
    if (status === 'pending') return 'Pending';
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  return (
    <div className="p-8 rounded-2xl bg-black/40 backdrop-blur-xl border border-white/10 shadow-2xl" data-testid="building-preview">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center">
          <Smartphone className="w-5 h-5 text-accent" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-white" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Building Artifacts
          </h3>
          <p className="text-sm text-muted-foreground">Compiling and optimizing builds</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {artifacts.map((artifact, index) => (
          <motion.div
            key={artifact.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.2 }}
            className="p-4 rounded-xl bg-white/5 border border-white/5 hover:border-white/10 transition-all"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{artifact.icon}</span>
                <div>
                  <div className="text-sm font-medium text-white font-mono">{artifact.name}</div>
                  <div className="text-xs text-muted-foreground">{artifact.size}</div>
                </div>
              </div>
              <div className={`px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(statuses[artifact.id])}`}>
                {statuses[artifact.id] !== 'complete' && statuses[artifact.id] !== 'pending' && (
                  <Loader2 className="w-3 h-3 inline mr-1 animate-spin" />
                )}
                {getStatusText(statuses[artifact.id])}
              </div>
            </div>
            
            {statuses[artifact.id] !== 'pending' && (
              <div className="relative h-1 bg-white/5 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: '0%' }}
                  animate={{ width: statuses[artifact.id] === 'complete' ? '100%' : '60%' }}
                  transition={{ duration: 0.8 }}
                  className={`absolute inset-y-0 left-0 ${
                    statuses[artifact.id] === 'complete' ? 'bg-green-500' : 'bg-accent'
                  } rounded-full`}
                ></motion.div>
              </div>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  );
};

// Step 5: Deploying Preview
const DeployingPreviewUI = () => {
  return (
    <div className="p-8 rounded-2xl bg-black/40 backdrop-blur-xl border border-white/10 shadow-2xl" data-testid="deploying-preview">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
          <Rocket className="w-5 h-5 text-green-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-white" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Preview Ready
          </h3>
          <p className="text-sm text-muted-foreground">Your applications are deployed</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="group relative overflow-hidden rounded-xl border border-white/10 bg-white/5"
        >
          <div className="aspect-video bg-gradient-to-br from-purple-500/10 to-blue-500/10 relative">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center space-y-2">
                <div className="w-16 h-16 mx-auto rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center">
                  <span className="text-2xl">🌐</span>
                </div>
                <div className="text-sm text-muted-foreground">Website Preview</div>
              </div>
            </div>
          </div>
          <div className="p-4 space-y-3">
            <div>
              <h4 className="font-semibold text-white mb-1">Website Preview</h4>
              <p className="text-xs text-muted-foreground font-mono">https://preview.example.com/website</p>
            </div>
            <button
              disabled
              className="w-full py-2.5 rounded-lg bg-primary/10 text-primary border border-primary/20 text-sm font-medium flex items-center justify-center gap-2 opacity-60 cursor-not-allowed"
            >
              <ExternalLink className="w-4 h-4" />
              Open Website Preview
            </button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4 }}
          className="group relative overflow-hidden rounded-xl border border-white/10 bg-white/5"
        >
          <div className="aspect-video bg-gradient-to-br from-blue-500/10 to-cyan-500/10 relative">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center space-y-2">
                <div className="w-16 h-16 mx-auto rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center">
                  <span className="text-2xl">⚙️</span>
                </div>
                <div className="text-sm text-muted-foreground">Admin Panel</div>
              </div>
            </div>
          </div>
          <div className="p-4 space-y-3">
            <div>
              <h4 className="font-semibold text-white mb-1">Admin Panel Preview</h4>
              <p className="text-xs text-muted-foreground font-mono">https://preview.example.com/admin</p>
            </div>
            <button
              disabled
              className="w-full py-2.5 rounded-lg bg-accent/10 text-accent border border-accent/20 text-sm font-medium flex items-center justify-center gap-2 opacity-60 cursor-not-allowed"
            >
              <ExternalLink className="w-4 h-4" />
              Open Admin Panel
            </button>
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="mt-6 p-4 rounded-xl bg-green-500/10 border border-green-500/20 flex items-start gap-3"
      >
        <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h5 className="text-sm font-semibold text-green-400 mb-1">Build Successful</h5>
          <p className="text-xs text-green-400/80">All platforms ready for download. Preview links will be available in the Previews section.</p>
        </div>
      </motion.div>
    </div>
  );
};
